import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, Inject, Input, NgModule, OnDestroy, OnInit, Optional } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { Router } from '@angular/router';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { Utilities } from '@app/common/utilities';
import { ActionLinkModule } from '@app/components/actions/action-link/action-link.component';
import { ActionRouterLinkModule } from '@app/components/actions/action-router-link/action-router-link.component';
import { ErrorTemplatesModule } from '@app/components/error-templates/error-templates.component';
import { TextPlaceholderModule } from '@app/components/text-placeholder/text-placeholder.component';
import { WidgetPlaceholderModule } from '@app/components/widget-placeholder/widget-placeholder.component';
import { ImgSrcProxyModule } from '@app/directives/img-src-proxy.directive';
import { NewsPage } from '@app/models/news-page';
import { LinkModule } from '@app/directives/link.directive';
import { ComponentStatesOnRoutesService } from '@app/services/component-states-on-routes.service';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';
import { LicenseService } from '@app/services/license.service';
import { NewsService } from '@app/services/news.service';
import { UserLanguageTermService } from '@app/services/user-language-term.service';
import { Environment } from '@environments/interface.environment';
import { forkJoin, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, map, mergeMap, startWith, switchMap, tap } from 'rxjs/operators';
import { trigger, style, transition, animate } from '@angular/animations';
import { BaseTrackingService } from '@app/tracking/base-tracking.service';
import { TrackingEvent } from '@app/models/tracking-event';
import { Constants } from '@app/common/constants';

@Component({
  selector: 'app-featured-news',
  templateUrl: './featured-news.component.html',
  styleUrls: ['./featured-news.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ transform: 'translateY(70%)' }),
        animate('200ms ease-in', style({ transform: 'translateY(0)' } )),
      ]),
      transition(':leave', [
        animate('200ms ease-out', style({ transform: 'translateY(100%)' }))
      ])
    ])
  ]
})
export class FeaturedNewsComponent implements OnInit, OnDestroy {

  @Input() amountOfDaysBackInTime = 365;
  @Input() amountOfNewsToDisplay = 0;
  @Input() titleMaxLength = 60;
  @Input() maxNumberOfShowMore = 2; // How may times can you press the show more button

  fetchedAmountOfNews = 0;
  amountOfNewsToIncreaseWith = this.amountOfNewsToDisplay;
  newsCenterUrl: string;
  amountOfNewsToDisplayCurrently: number;
  featuredNewsItems$: Observable<Array<NewsPage>>;
  loading = false;
  private moreNewsSubject = new Subject<number>();
  private errorSubject$ = new Subject();
  featureSettings = this.featureSettingsStoreService.state;
  objectFitFallback = false;
  fetchFromGraph = true;
  isUserLicensed$: Observable<boolean>;
  workplaceName: string;
  numberOfShowMoreClicks = 0;
  showNewsDesc = this.featureSettingsStoreService.state.showNewsDescription;
  isQuickView = false;
  amountOfNewsToDisplayConfigured = this.amountOfNewsToDisplay > 0;
  subscription: Subscription;
  componentName = 'Featured news';

  constructor(@Inject(CUSTOMERENVIRONMENT) private environment: Environment,
              @Inject(DOCUMENT) private document: Document,
              private service: NewsService,
              private router: Router,
              private componentEventService: ComponentStatesOnRoutesService,
              private featureSettingsStoreService: FeatureSettingsStoreService,
              public elementRef: ElementRef,
              private licenseService: LicenseService,
              private userLanguageTermService: UserLanguageTermService,
              private breakpointObserver: BreakpointObserver,
              @Optional() private trackingService?: BaseTrackingService) {
    this.workplaceName = this.featureSettingsStoreService.state.workplaceName;
  }

  ngOnInit() {
    // Vary amount of news to load based on screen size to ensure display is even
    this.subscription = this.breakpointObserver.observe([Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge])
      .subscribe((state: BreakpointState) => {
        if (!this.amountOfNewsToDisplayConfigured) { // amountOfNewsToDisplay is not configured by customers
          if (state.breakpoints[Breakpoints.Medium]) {
            this.amountOfNewsToDisplay = 3;
            this.amountOfNewsToDisplayCurrently = this.amountOfNewsToDisplay;
            this.moreNewsSubject.next(this.amountOfNewsToDisplayCurrently + 1);
          } else {
            this.amountOfNewsToDisplay = 4;
            this.amountOfNewsToDisplayCurrently = this.amountOfNewsToDisplay;
            this.moreNewsSubject.next(this.amountOfNewsToDisplayCurrently + 1);
          }
        } else {
          this.amountOfNewsToDisplayCurrently = this.amountOfNewsToDisplay;
          this.moreNewsSubject.next(this.amountOfNewsToDisplayCurrently + 1);
        }

        this.amountOfNewsToIncreaseWith = this.amountOfNewsToDisplay;
      });

    this.newsCenterUrl = `${this.featureSettingsStoreService.state.newsArchiveLink}`;
    this.amountOfNewsToDisplayCurrently = this.amountOfNewsToDisplay;

    const componentEvent = this.componentEventService.state.RouteMap.has(this.router.url) && this.componentEventService.state.RouteMap.get(this.router.url).find(x => x.ComponentId === this.elementRef.nativeElement.localName);
    const componentEventData = componentEvent ? componentEvent.Data as Array<NewsPage> : undefined;

    if ('objectFit' in this.document.documentElement.style === false) {
      this.objectFitFallback = true;
    }

    const subjectsToListenOn$ = this.moreNewsSubject
      .pipe(map((amount) => {
        this.service.reloadAllTopNews();
        return Number.isInteger(amount) ? Number(amount) : this.amountOfNewsToDisplayCurrently + 1;
      }));

    this.featuredNewsItems$ = subjectsToListenOn$
      .pipe(
        startWith(this.amountOfNewsToDisplayCurrently + 1),
        switchMap((amount) => {
          this.loading = true;
          const userLanguageTerms$ = this.userLanguageTermService.getUserLanguageTerms();
          if (this.environment.enableWorkplaceLight) {
            // Workplace light enabled
            return this.licenseService.hasUserAny365License()
              .pipe(
                mergeMap(licensed => {
                  if (licensed) {
                    // Workplace light + SharePoint licensed
                    return forkJoin([
                      this.service.getTopNewsFromApi(amount, userLanguageTerms$),
                      this.service.getModernTopNewsFromGraph(amount, userLanguageTerms$)
                    ])
                      .pipe(
                        map(([apiItems, graphItems]) => {
                          // Concat the results from sharepoint + api and resort them, the dates seem to still be strings so we have to parse them again
                          return apiItems.concat(graphItems).sort((a, b) => new Date(b.PublishedDate).getTime() - new Date(a.PublishedDate).getTime());
                        })
                      );
                  } else {
                    // Only Workplace Light
                    return this.service.getTopNewsFromApi(amount, userLanguageTerms$);
                  }
                })
              );
          } else {
            // Only SharePoint
            return this.service.getModernTopNewsFromGraph(amount, userLanguageTerms$);
          }
        }),
        map(pages => {
          this.fetchedAmountOfNews = pages.length;
          if (this.fetchedAmountOfNews > this.amountOfNewsToDisplayCurrently) {
            pages = pages.slice(0, this.amountOfNewsToDisplayCurrently);
          }
          return pages.map(page => {
            if (componentEventData) {
              const foundNewsPage = componentEventData.find(ced => ced.Path === page.Path);
              if (foundNewsPage) {
                page.ShowNewOrModifiedBadge = true;
              }
            } else {
              page.ShowNewOrModifiedBadge = false;
            }
            return page;
          });
        }),
        tap(() => {
          this.loading = false;
        }),
        catchError(err => {
          this.errorSubject$.next(err);
          return of(null);
        }));
  }

  setFallBackStyle(newsItem: HTMLImageElement) {
    if (this.objectFitFallback) {
      newsItem.parentElement.style.backgroundImage = `url(${newsItem.src})`;
      newsItem.parentElement.style.backgroundSize = `cover`;
      newsItem.parentElement.style.backgroundPosition = `50% 50%`;
      return {
        'display': `none`
      };
    }
  }

  showMoreNews() {
    this.numberOfShowMoreClicks++;
    this.amountOfNewsToDisplayCurrently = this.amountOfNewsToDisplayCurrently + this.amountOfNewsToIncreaseWith;
    this.moreNewsSubject.next(this.amountOfNewsToDisplayCurrently + 1);
  }

  showLessNews() {
    this.numberOfShowMoreClicks = 0;
    this.amountOfNewsToDisplayCurrently = this.amountOfNewsToDisplay;
    this.moreNewsSubject.next(this.amountOfNewsToDisplayCurrently + 1);
    this.scrollIntoFeatureView();
  }

  scrollIntoFeatureView() {
    const elementId = 'app-featured-news';
    const element = this.document.getElementById(elementId);

    if (element) {
      if (!/^(?:a|select|input|button|textarea)$/i.test(element.tagName)) {
        element.scrollIntoView();
      }
      element.focus();
    }
  }

  showNewsDescription(newsItem: NewsPage) {
    if (newsItem.hide !== undefined) {
      newsItem.hide = !newsItem.hide;
    } else {
      newsItem.hide = true;
      if (this.trackingService && this.trackingService.trackEvent) {
        const trackingEvent = new TrackingEvent({
          name: Constants.TRACKING_EVENT_TYPE_NEWS_DESCR_CLICK,
          properties: {
            href: newsItem.Path,
            label: newsItem.Title,
            componentId: this.elementRef.nativeElement.localName
          }
        });
        this.trackingService.trackEvent(trackingEvent);
      }
    }
  }

  getSenderTagString(newsItem: NewsPage) {
    return Utilities.getSenderTagString(newsItem);
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
@NgModule({
  declarations: [FeaturedNewsComponent],
  exports: [
    FeaturedNewsComponent
  ],
  imports: [
    CommonModule,
    ImgSrcProxyModule,
    ActionLinkModule,
    ActionRouterLinkModule,
    TextPlaceholderModule,
    ErrorTemplatesModule,
    WidgetPlaceholderModule,
    MatButtonModule,
    LinkModule,
  ]
})
export class FeaturedNewsModule { }
